import React from "react";
import { Box, Typography, Container, Button } from "@mui/material";

const UrlEncoderContent = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Container maxWidth="md" sx={{ textAlign: "center", mt: 4 }}>
      {/* Intro */}
      {/* SEO Title & Subtitle */}
      <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
        URL Encoder – Encode URLs Safely for Web & API Use
      </Typography>
      <Typography
        variant="h6"
        component="h2"
        color="text.secondary"
        gutterBottom
      >
        Quickly convert your URLs into encoded format for safe transmission and
        sharing on the web
      </Typography>

      <Typography variant="body1" paragraph>
        Need to encode URLs to ensure special characters are properly
        transmitted in web requests? Our URL encoder tool instantly converts
        your URLs into a safe, encoded format for use in browsers, APIs, and
        applications.
      </Typography>
      <Typography variant="body1" paragraph>
        Whether you're a developer, tester, or marketer, our URL encoder
        simplifies your workflow by handling encoding automatically so your URLs
        always work correctly.
      </Typography>

      {/* Features */}
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Why Use Our URL Encoder Online Tool?
      </Typography>
      <ul style={{ textAlign: "left", display: "inline-block" }}>
        <li>🔒 Encode special characters in URLs safely and correctly</li>
        <li>⚡ Fast and reliable encoding for any URL length</li>
        <li>🔗 Ensure compatibility across browsers, APIs, and web services</li>
        <li>📥 Copy encoded URLs easily or download them for sharing</li>
      </ul>

      {/* How It Works */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={4}>
        How to Use the URL Encoder
      </Typography>
      <ol style={{ textAlign: "left", display: "inline-block" }}>
        <li>Paste or enter your URL into the input field.</li>
        <li>Click “Encode URL” to transform your URL into encoded format.</li>
        <li>Copy the encoded URL or use it directly in your web requests.</li>
      </ol>

      {/* SEO Paragraph */}
      <Typography variant="body1" paragraph mt={4}>
        Our <b>URL encoder</b> tool is trusted by web developers, marketers, and
        professionals worldwide to prepare URLs for safe transmission. It
        handles spaces, special characters, and non-ASCII text efficiently so
        your URLs remain functional and readable by web browsers and APIs.
      </Typography>

      <Typography variant="body1" paragraph>
        Don’t risk broken links or errors due to unencoded URLs—let our{" "}
        <b>URL encoder</b> do the work for you so your links always stay intact.
      </Typography>

      {/* FAQs */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={4}>
        Frequently Asked Questions (FAQs)
      </Typography>

      <Typography variant="body1" fontWeight="bold">
        🔍 What does URL encoding mean?
      </Typography>
      <Typography variant="body2" paragraph>
        URL encoding converts special characters into a format that can be
        transmitted over the Internet, using percent (%) signs followed by
        hexadecimal codes.
      </Typography>

      <Typography variant="body1" fontWeight="bold">
        🆓 Is the URL encoder free to use?
      </Typography>
      <Typography variant="body2" paragraph>
        Free plan users will have credits deducted per encoding action. Paid
        plan users enjoy unlimited use without credits.
      </Typography>

      <Typography variant="body1" fontWeight="bold">
        📦 What kind of URLs can I encode?
      </Typography>
      <Typography variant="body2" paragraph>
        You can encode any valid URL, including those containing spaces,
        symbols, query parameters, and non-English characters.
      </Typography>

      <Typography variant="body1" fontWeight="bold">
        🔄 Can I decode URLs here too?
      </Typography>
      <Typography variant="body2" paragraph>
        Yes! Use our separate{" "}
        <a
          href="/url-decoder-online"
          style={{ color: "#1976d2", textDecoration: "underline" }}
        >
          URL Decoder tool
        </a>{" "}
        to convert encoded URLs back to their original format.
      </Typography>

      <Typography variant="body1" fontWeight="bold">
        🔧 Why should I encode URLs?
      </Typography>
      <Typography variant="body2" paragraph>
        Encoding URLs prevents errors caused by special characters in web
        requests and ensures your links work consistently across different
        platforms and browsers.
      </Typography>

      {/* CTA */}
      <Box mt={4}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={scrollToTop}
        >
          Encode Your URL Now
        </Button>
      </Box>
    </Container>
  );
};

export default UrlEncoderContent;
